import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatDividerModule } from "@angular/material/divider";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatButtonModule } from "@angular/material/button";
import { DragDropModule, CdkDragDrop } from "@angular/cdk/drag-drop";
import { Viewerlayer } from "../../../model/viewerlayer";
import { IProjectAOI } from "../../../interfaces/i-project-aoi";
import { FeatureFlagsService } from "../../../services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../../model/feature-flags";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";

/**
 * Layer Overlay Panel Component (Standalone)
 * Reusable panel for layer management with imagery/basemap toggle,
 * gray sky contrast, and drag & drop overlay layers.
 * Duplicates functionality from LayerManagerWidgetComponent for standalone use.
 */
@Component({
  selector: "app-layer-overlay-panel",
  standalone: true,
  imports: [
    CommonModule,
    MatDividerModule,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    DragDropModule,
  ],
  templateUrl: "./layer-overlay-panel.component.html",
  styleUrl: "./layer-overlay-panel.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayerOverlayPanelComponent {
  viewerService = inject(VIEWER_SERVICE);

  flagService = inject(FeatureFlagsService);

  FeatureFlags = FeatureFlags;

  @Input() isImageryShown: boolean = true;

  @Input() imageryIcon: string = "assets/images/imagery-icon.png";

  @Input() baseMapIcon: string = "assets/images/basemap-icon.png";

  @Input() isActivatedAutoContrast: boolean = false;

  @Input() centerAOI: IProjectAOI;

  @Input() overlayLayers: Viewerlayer[] = [];

  @Input() autoContrastAllowed: boolean = false;

  @Input() resize: boolean = false;

  @Input() dualViewEnabled: boolean = false;

  @Output() imageryToggle = new EventEmitter<void>();

  @Output() basemapToggle = new EventEmitter<void>();

  @Output() contrastToggle = new EventEmitter<boolean>();

  @Output() overlayOrderChange = new EventEmitter<CdkDragDrop<Viewerlayer[]>>();

  @Output() layerVisibilityChange = new EventEmitter<{ layer: Viewerlayer; visible: boolean }>();

  @Output() childVisibilityChange = new EventEmitter<any>();

  @Output() featureFit = new EventEmitter<{ layer: any; feature: any }>();

  @Output() bboxFit = new EventEmitter<{ bbox: any; fitOptions: any }>();

  @Output() layerRemove = new EventEmitter<Viewerlayer>();

  /**
   * Handles imagery button click
   */
  onImageryClick(): void {
    this.imageryToggle.emit();
  }

  /**
   * Handles basemap button click
   */
  onBasemapClick(): void {
    this.basemapToggle.emit();
  }

  /**
   * Handles contrast checkbox toggle
   */
  onContrastToggle(checked: boolean): void {
    this.contrastToggle.emit(checked);
  }

  /**
   * Handles drag & drop reordering of overlay layers
   */
  onOverlayOrderChange(event: CdkDragDrop<Viewerlayer[]>): void {
    this.overlayOrderChange.emit(event);
  }

  /**
   * Handles layer visibility checkbox change
   */
  onLayerVisibilityChange(layer: Viewerlayer, visible: boolean): void {
    this.layerVisibilityChange.emit({ layer, visible });
  }

  /**
   * Handles child layer visibility toggle
   */
  onChildVisibilityChange(event: any): void {
    this.childVisibilityChange.emit(event);
  }

  /**
   * Handles zoom to feature
   */
  onZoomToFeature(layer: any, feature: any): void {
    this.featureFit.emit({ layer, feature });
  }

  /**
   * Handles fit to bounding box
   */
  onFitToBbox(bbox: any, fitOptions: any): void {
    this.bboxFit.emit({ bbox, fitOptions });
  }

  /**
   * Handles layer removal
   */
  onRemoveLayer(layer: Viewerlayer): void {
    this.layerRemove.emit(layer);
  }

  /**
   * Toggles layer visibility
   */
  toggleLayerVisibility(layer: Viewerlayer): void {
    const newVisibility = !layer.visible;
    this.onLayerVisibilityChange(layer, newVisibility);
  }

  /**
   * Gets layer opacity as percentage
   */
  getLayerOpacity(layer: Viewerlayer): number {
    return layer.opacity ? Math.round(layer.opacity * 100) : 100;
  }

  /**
   * Checks if layer has child layers
   */
  hasChildLayers(layer: Viewerlayer): boolean {
    return !!(layer.childs && layer.childs.length > 0);
  }

  /**
   * Gets child layers visibility status
   */
  getChildLayersVisible(layer: Viewerlayer): boolean {
    if (!this.hasChildLayers(layer)) return false;
    return layer.childs.some((child) => child.visible);
  }
}
